import * as React from 'react';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  useGridApiContext,
} from '@mui/x-data-grid';
import { Box, TextField, InputAdornment, Button } from '@mui/material';
import { Search as SearchIcon, ViewColumn as ColumnsIcon, Delete as DeleteIcon } from '@mui/icons-material';

export default function CustomDataGridToolbar(props) {
  const apiRef = useGridApiContext();
  
  // Default props for backward compatibility
  const {
    searchInput = { isShow: true },
    columnButton = { isShow: true },
    createButton = { isShow: false },
    deleteButton = { isShow: false },
    startAdornment = null,
    onSearchChange, // Optional callback for server-side search
    currentSearchValue = '' // Current search value (for URL persistence)
  } = props || {};
  
  const [searchValue, setSearchValue] = React.useState(currentSearchValue);

  // Update search value when currentSearchValue prop changes (e.g., from URL)
  React.useEffect(() => {
    setSearchValue(currentSearchValue);
  }, [currentSearchValue]);

  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setSearchValue(value);
    
    // Trim the search value to remove leading/trailing whitespace
    const trimmedValue = value.trim();
    
    if (onSearchChange) {
      onSearchChange(trimmedValue);
    } else {
      apiRef.current.setQuickFilterValues(trimmedValue ? [trimmedValue] : []);
    }
  }, [apiRef, onSearchChange]);

  // Handle file input creation for upload functionality
  const renderFileInput = () => {
    if (!createButton.isShow || !createButton.inputRef) return null;
    
    const inputProps = {
      ref: createButton.inputRef,
      type: "file",
      style: { display: "none" },
      onChange: createButton.onChange
    };

    // Configure input based on file type
    if (createButton.fileType === "CNS" || createButton.fileType === "NATUS") {
      inputProps.webkitdirectory = "";
      inputProps.directory = "";
      inputProps.multiple = true;
    } else {
      inputProps.accept = createButton.fileType === "MEDICATIONS" ? ".csv,.xlsx,.xls" : ".edf";
    }

    return React.createElement("input", inputProps);
  };

  return (
    <GridToolbarContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          p: 1,
          gap: 2,
        }}
      >
        {/* Custom action buttons on the left */}
        {(createButton.isShow || deleteButton.isShow) && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {createButton.isShow && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={createButton.onClick}
                  sx={{
                    backgroundColor: 'var(--color-accent-interactive)',
                    color: 'var(--color-surface-panel)',
                    '&:hover': {
                      backgroundColor: 'var(--color-accent-interactive)',
                      opacity: 0.85,
                    },
                    textTransform: 'none',
                    fontSize: '14px',
                    height: 36,
                  }}
                >
                  {createButton.title}
                </Button>
                {renderFileInput()}
              </>
            )}
            {deleteButton.isShow && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={deleteButton.onClick}
                sx={{
                  color: '#dc3545',
                  borderColor: '#dc3545',
                  '&:hover': {
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    color: 'white',
                  },
                  textTransform: 'none',
                  fontSize: '14px',
                  height: 36,
                }}
              >
                Delete
              </Button>
            )}
          </Box>
        )}

        {startAdornment}

        {searchInput.isShow && (
          <TextField
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={searchInput.placeholder || "Search..."}
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                height: 36,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
        
        {/* Right side: Fixed width buttons */}
        {columnButton.isShow && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            
            <Box
              sx={{
                '& .MuiButton-root': {
                  color: 'var(--color-accent-interactive)',
                  padding: '0px 10px',
                  border: '1px solid var(--color-accent-interactive)',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'var(--color-accent-interactive)',
                    border: '1px solid var(--color-accent-interactive)',
                    color: 'var(--color-surface-panel)',
                  },
                  '&:active': {
                    backgroundColor: 'var(--color-accent-interactive)',
                    border: '1px solid var(--color-accent-interactive)',
                    color: 'var(--color-surface-panel)',
                  },
                  textTransform: 'none',
                  fontSize: '14px',
                  height: 36,
                  minWidth: 'auto',
                  '& .MuiButton-startIcon': {
                    marginRight: '4px',
                  },
                },
              }}
            >
              <GridToolbarColumnsButton 
                startIcon={<ColumnsIcon />}
                variant="outlined"
                size="small"
              >
                Columns
              </GridToolbarColumnsButton>
            </Box>
          </Box>
        )}
      </Box>
    </GridToolbarContainer>
  );
} 